import type { CompetenceStepResult } from "../api/client";
import { Callout } from "../components/Callout";

/**
 * The server's advisory findings for one step of the competence record.
 *
 * Advisories are recorded consequences, not refusals: an expired certification, a
 * competence claimed below lead auditor, no witnessed audit yet, AI accountability not
 * accepted. None of them stops the record being written down, and each says what it costs
 * — because an auditor should learn that a claim narrows what they can be allocated to
 * here, not from a blocked allocation months later.
 *
 * A near-twin of the certification body's `Advisories`, and separate for the same reason
 * the issue types are: the two flows have different steps, and a shared component would
 * have to take a union it cannot narrow.
 *
 * **A list of results rather than one**, because a stop on the rail may stand for more than
 * one server step — competency stands for two. The consequences read as one block under the
 * fields that caused them, which is what they were when the fields were on two screens.
 */
export function Advisories({ results }: { results: readonly CompetenceStepResult[] }) {
  const advisories = results.flatMap((result) => result.advisory);
  if (advisories.length === 0) return null;

  return (
    <>
      {advisories.map((issue) => (
        <Callout key={`${issue.field}:${issue.detail}`} tone="warn">
          {issue.detail}
          {issue.reference ? <span className="src">{issue.reference}</span> : null}
        </Callout>
      ))}
    </>
  );
}
